import { formatCompactNumber } from './formatters/format-compact-number';
import { formatDirection } from './formatters/format-direction';
import { formatDuration } from './formatters/format-duration';
import { formatPid } from './formatters/format-pid';
import { formatPsigInhg } from './formatters/format-psig-inhg';
import { formatUnitless } from './formatters/format-unitless';
import { UnitCatalogEntry } from './shared/types';
import { transformAcceleration } from './transformers/transform-acceleration';
import { transformAngle } from './transformers/transform-angle';
import { transformAngularVelocity } from './transformers/transform-angular-velocity';
import { transformArea } from './transformers/transform-area';
import { transformBoolean } from './transformers/transform-boolean';
import { transformConcentration } from './transformers/transform-concentration';
import { transformDensity } from './transformers/transform-density';
import { transformDuration } from './transformers/transform-duration';
import {
  transformCurrent,
  transformResistance,
  transformVoltage,
} from './transformers/transform-electrical';
import {
  transformApparentEnergy,
  transformEnergy,
  transformReactiveEnergy,
} from './transformers/transform-energy';
import { transformForce } from './transformers/transform-force';
import { transformFrequency } from './transformers/transform-frequency';
import { transformLength } from './transformers/transform-length';
import { transformMass } from './transformers/transform-mass';
import { transformMassFlow } from './transformers/transform-mass-flow';
import { transformNone } from './transformers/transform-none';
import { transformOnOff } from './transformers/transform-on-off';
import { transformOpenClosed } from './transformers/transform-open-closed';
import { transformPower } from './transformers/transform-power';
import { transformPressure } from './transformers/transform-pressure';
import { transformPsigInhg } from './transformers/transform-psig-inhg';
import { transformTemperature } from './transformers/transform-temperature';
import { transformTime } from './transformers/transform-time';
import { transformTorque } from './transformers/transform-torque';
import { transformVelocity } from './transformers/transform-velocity';
import {
  transformDynamicViscosity,
  transformKinematicViscosity,
} from './transformers/transform-viscosity';
import { transformVolume } from './transformers/transform-volume';
import {
  transformStandardVolumeFlow,
  transformVolumeFlow,
} from './transformers/transform-volume-flow';
export const UNIT_CATALOG: UnitCatalogEntry[] = [
  // Temperature
  {
    name: 'Celsius',
    asciiSymbol: 'C',
    aliases: ['°C'],
    transform: transformTemperature,
    formatter: { precision: 1, unitSuffix: '°C' },
  },
  {
    name: 'Fahrenheit',
    asciiSymbol: 'F',
    aliases: ['°F'],
    transform: transformTemperature,
    formatter: { precision: 1, unitSuffix: '°F' },
  },

  // Temperature
  {
    name: 'Kelvin',
    asciiSymbol: 'K',
    aliases: ['°k'],
    transform: transformTemperature,
    formatter: { precision: 1, unitSuffix: 'K' },
  },
  {
    name: 'Rankine',
    asciiSymbol: 'R',
    aliases: ['°R'],
    transform: transformTemperature,
    formatter: { precision: 1, unitSuffix: '°R' },
  },

  // Time
  {
    name: 'Milliseconds',
    asciiSymbol: 'ms',
    transform: transformTime,
    formatter: { precision: 0, unitSuffix: 'ms' },
  },

  // Time (pivot = seconds).
  {
    name: 'hrs',
    asciiSymbol: 'hr',
    transform: transformTime,
    formatter: { precision: 2, unitSuffix: 'hrs' },
  },
  {
    name: 'min',
    asciiSymbol: 'min',
    transform: transformTime,
    formatter: { precision: 1, unitSuffix: 'min' },
  },
  {
    name: 'sec',
    asciiSymbol: 'sec',
    transform: transformTime,
    formatter: { precision: 0, unitSuffix: 'sec' },
  },
  {
    name: 'duration',
    asciiSymbol: 'duration',
    aliases: ['timer'],
    inputUnits: 'sec',
    transform: transformDuration,
    formatter: formatDuration,
  },

  // Length & Distance
  {
    name: 'Inches',
    asciiSymbol: 'in',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'in' },
  },
  {
    name: 'Feet',
    asciiSymbol: 'ft',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'ft' },
  },
  {
    name: 'Yards',
    asciiSymbol: 'yd',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'yd' },
  },
  {
    name: 'Miles',
    asciiSymbol: 'mi',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'mi' },
  },
  {
    name: 'Millimeters',
    asciiSymbol: 'mm',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'mm' },
  },
  {
    name: 'Centimeters',
    asciiSymbol: 'cm',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'cm' },
  },
  {
    name: 'Meters',
    asciiSymbol: 'm',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'm' },
  },
  {
    name: 'Kilometers',
    asciiSymbol: 'km',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'km' },
  },
  {
    name: 'Thousandths of an inch',
    asciiSymbol: 'mil',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'mil' },
  },
  {
    name: 'Micrometers',
    asciiSymbol: 'um',
    aliases: ['µm'],
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'µm' },
  },
  {
    name: 'Nanometers',
    asciiSymbol: 'nm',
    transform: transformLength,
    formatter: { precision: 1, unitSuffix: 'nm' },
  },

  // Velocity & Speed
  {
    name: 'Millimeters per second',
    asciiSymbol: 'mm/s',
    transform: transformVelocity,
    formatter: { precision: 0, unitSuffix: 'mm/s' },
  },
  {
    name: 'Inches per second',
    asciiSymbol: 'in/s',
    transform: transformVelocity,
    formatter: { precision: 1, unitSuffix: 'in/s' },
  },
  {
    name: 'Feet per second',
    asciiSymbol: 'ft/s',
    transform: transformVelocity,
    formatter: { precision: 1, unitSuffix: 'ft/s' },
  },
  {
    name: 'Meters per second',
    asciiSymbol: 'm/s',
    transform: transformVelocity,
    formatter: { precision: 1, unitSuffix: 'm/s' },
  },
  {
    name: 'Miles per hour',
    asciiSymbol: 'mph',
    transform: transformVelocity,
    formatter: { precision: 1, unitSuffix: 'MPH' },
  },
  {
    name: 'Kilometers per hour',
    asciiSymbol: 'km/h',
    transform: transformVelocity,
    formatter: { precision: 1, unitSuffix: 'km/h' },
  },
  {
    name: 'Revolutions per second',
    asciiSymbol: 'rps',
    transform: transformAngularVelocity,
    formatter: { precision: 0, unitSuffix: 'rps' },
  },
  {
    name: 'Radians per second',
    asciiSymbol: 'rad/s',
    transform: transformAngularVelocity,
    formatter: { precision: 1, unitSuffix: 'rad/s' },
  },

  // Acceleration
  {
    name: 'Meters per second squared',
    asciiSymbol: 'm/s2',
    aliases: ['m/s²'],
    transform: transformAcceleration,
    formatter: { precision: 1, unitSuffix: 'm/s²' },
  },
  {
    name: 'Inches per second squared',
    asciiSymbol: 'in/s2',
    aliases: ['in/s²'],
    transform: transformAcceleration,
    formatter: { precision: 1, unitSuffix: 'in/s²' },
  },
  {
    name: 'Feet per second squared',
    asciiSymbol: 'ft/s2',
    aliases: ['ft/s²'],
    transform: transformAcceleration,
    formatter: { precision: 1, unitSuffix: 'ft/s²' },
  },
  {
    name: 'Millimeters per second squared',
    asciiSymbol: 'mm/s2',
    aliases: ['mm/s²'],
    transform: transformAcceleration,
    formatter: { precision: 1, unitSuffix: 'mm/s²' },
  },
  {
    name: 'G-force (standard gravity)',
    asciiSymbol: 'g_n',
    aliases: ['gₙ'],
    transform: transformAcceleration,
    formatter: { precision: 1, unitSuffix: 'gₙ' },
  },

  // Volume & Flow
  {
    name: 'Gallons',
    asciiSymbol: 'gal',
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'gal' },
  },
  {
    name: 'Gallons per minute',
    asciiSymbol: 'gpm',
    transform: transformVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'GPM' },
  },
  {
    name: 'Gallons per day',
    asciiSymbol: 'gpd',
    transform: transformVolumeFlow,
    formatter: { precision: 1, unitSuffix: 'GPD' },
  },
  {
    name: 'Pounds per minute',
    asciiSymbol: 'lb/min',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'lb/min' },
  },
  {
    name: 'Milliliters per minute',
    asciiSymbol: 'mL/min',
    transform: transformVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'mL/min' },
  },
  {
    name: 'Liters per minute',
    asciiSymbol: 'L/min',
    transform: transformVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'L/min' },
  },
  {
    name: 'Liters per day',
    asciiSymbol: 'L/d',
    transform: transformVolumeFlow,
    formatter: { precision: 1, unitSuffix: 'L/d' },
  },
  {
    name: 'Cubic meters per hour',
    asciiSymbol: 'm3/h',
    aliases: ['m³/h'],
    transform: transformVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'm³/h' },
  },
  {
    name: 'Cubic feet per minute',
    asciiSymbol: 'cfm',
    transform: transformVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'CFM' },
  },
  {
    name: 'Standard cubic feet per minute',
    asciiSymbol: 'scfm',
    transform: transformStandardVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'SCFM' },
  },
  {
    name: 'Normal cubic meters per hour',
    asciiSymbol: 'Nm3/h',
    aliases: ['Nm³/h'],
    transform: transformStandardVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'Nm³/h' },
  },
  {
    name: 'Standard liters per minute',
    asciiSymbol: 'slpm',
    transform: transformStandardVolumeFlow,
    formatter: { precision: 2, unitSuffix: 'slpm' },
  },
  {
    name: 'Kilograms per second',
    asciiSymbol: 'kg/s',
    transform: transformMassFlow,
    formatter: { precision: 3, unitSuffix: 'kg/s' },
  },

  // Pressure
  {
    name: 'Pounds per square inch absolute',
    asciiSymbol: 'psia',
    transform: transformPressure,
    formatter: { precision: 2, unitSuffix: 'PSIA' },
  },
  {
    name: 'Pounds per square inch gauge',
    asciiSymbol: 'psig',
    transform: transformPressure,
    formatter: { precision: 2, unitSuffix: 'PSIG' },
  },
  {
    name: 'Pounds per square inch differential',
    asciiSymbol: 'psid',
    transform: transformPressure,
    formatter: { precision: 2, unitSuffix: 'PSID' },
  },
  {
    name: 'Pounds per square inch',
    asciiSymbol: 'psi',
    transform: transformPressure,
    formatter: { precision: 2, unitSuffix: 'PSI' },
  },
  {
    name: 'Bar',
    asciiSymbol: 'bar',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'bar' },
  },
  {
    name: 'Millibar',
    asciiSymbol: 'mbar',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'mbar' },
  },
  {
    name: 'Kilopascals',
    asciiSymbol: 'kPa',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'kPa' },
  },
  {
    name: 'Megapascals',
    asciiSymbol: 'MPa',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'MPa' },
  },
  {
    name: 'Inches water column',
    asciiSymbol: 'inH2O',
    aliases: ['inH₂O'],
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'inH₂O' },
  },
  {
    name: 'Millimeters mercury',
    asciiSymbol: 'mmHg',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'mmHg' },
  },
  {
    name: 'Torr',
    asciiSymbol: 'torr',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'Torr' },
  },
  {
    name: 'Atmospheres',
    asciiSymbol: 'atm',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'atm' },
  },
  {
    name: 'Pascals',
    asciiSymbol: 'Pa',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'Pa' },
  },
  {
    name: 'Inches of mercury',
    asciiSymbol: 'inHg',
    transform: transformPressure,
    formatter: { precision: 1, unitSuffix: 'inHG' },
  },
  {
    name: 'psig-inhg',
    asciiSymbol: 'psig-inhg',
    transform: transformPsigInhg,
    formatter: formatPsigInhg,
  },

  // Electrical
  {
    name: 'Volts of Alternating Current',
    asciiSymbol: 'VAC',
    transform: transformVoltage,
    formatter: { precision: 1, unitSuffix: 'VAC' },
  },
  {
    name: 'Volts of Direct Current',
    asciiSymbol: 'VDC',
    transform: transformVoltage,
    formatter: { precision: 1, unitSuffix: 'VDC' },
  },
  {
    name: 'V',
    asciiSymbol: 'V',
    transform: transformVoltage,
    formatter: { precision: 2, unitSuffix: 'V' },
  },
  {
    name: 'Millivolts',
    asciiSymbol: 'mV',
    transform: transformVoltage,
    formatter: { precision: 1, unitSuffix: 'mV' },
  },
  {
    name: 'Amperes',
    asciiSymbol: 'A',
    aliases: ['amps'],
    transform: transformCurrent,
    formatter: { precision: 2, unitSuffix: 'A' },
  },
  {
    name: 'Milliamperes',
    asciiSymbol: 'mA',
    transform: transformCurrent,
    formatter: { precision: 1, unitSuffix: 'mA' },
  },
  {
    name: 'Full Load Amperes',
    asciiSymbol: 'FLA',
    transform: transformCurrent,
    formatter: { precision: 1, unitSuffix: 'FLA' },
  },
  {
    name: 'Electrical Resistance',
    asciiSymbol: 'ohm',
    aliases: ['Ω'],
    transform: transformResistance,
    formatter: { precision: 2, unitSuffix: 'Ω' },
  },
  {
    name: 'Electrical Conductivity',
    asciiSymbol: 'uS/cm',
    aliases: ['µS/cm'],
    formatter: { precision: 2, unitSuffix: 'µS/cm' },
  },
  {
    name: 'Cycles per second',
    // Frequency (pivot = Hz; kHz/MHz/GHz are target-only, see transform-frequency.ts).
    asciiSymbol: 'Hz',
    transform: transformFrequency,
    formatter: { precision: 2, unitSuffix: 'Hz' },
  },
  {
    name: 'Volt-Amperes',
    asciiSymbol: 'VA',
    formatter: { precision: 2, unitSuffix: 'VA' },
  },
  {
    name: 'Volt-Amperes Reactive',
    asciiSymbol: 'VAR',
    formatter: { precision: 2, unitSuffix: 'VAR' },
  },
  {
    name: 'Decibels',
    asciiSymbol: 'dB',
    formatter: { precision: 1, unitSuffix: 'dB' },
  },
  {
    name: 'Decibel-milliwatts',
    asciiSymbol: 'dBm',
    formatter: { precision: 1, unitSuffix: 'dBm' },
  },
  {
    name: 'Megaohms',
    asciiSymbol: 'Mohm',
    aliases: ['MΩ'],
    transform: transformResistance,
    formatter: { precision: 2, unitSuffix: 'MΩ' },
  },
  {
    name: 'Kiloohms',
    asciiSymbol: 'kohm',
    aliases: ['kΩ'],
    transform: transformResistance,
    formatter: { precision: 2, unitSuffix: 'kΩ' },
  },
  {
    name: 'Microamps',
    asciiSymbol: 'uA',
    aliases: ['µA'],
    transform: transformCurrent,
    formatter: { precision: 1, unitSuffix: 'µA' },
  },
  {
    name: 'Microvolts',
    asciiSymbol: 'uV',
    aliases: ['µV'],
    transform: transformVoltage,
    formatter: { precision: 1, unitSuffix: 'µV' },
  },
  {
    name: 'Kilovolts',
    asciiSymbol: 'kV',
    transform: transformVoltage,
    formatter: { precision: 2, unitSuffix: 'kV' },
  },
  {
    name: 'Kiloamperes',
    asciiSymbol: 'kA',
    transform: transformCurrent,
    formatter: { precision: 2, unitSuffix: 'kA' },
  },
  {
    name: 'Kilohertz',
    asciiSymbol: 'kHz',
    transform: transformFrequency,
    formatter: { precision: 2, unitSuffix: 'kHz' },
  },
  {
    name: 'Megahertz',
    asciiSymbol: 'MHz',
    transform: transformFrequency,
    formatter: { precision: 2, unitSuffix: 'MHz' },
  },

  // Energy & Power
  {
    name: 'Watts',
    asciiSymbol: 'W',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'W' },
  },
  {
    name: 'Kilowatt',
    asciiSymbol: 'kW',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'kW' },
  },
  {
    name: 'Kilowatt Hour',
    asciiSymbol: 'kWh',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'kWh' },
  },
  {
    name: 'Kilovolt Ampere',
    asciiSymbol: 'kVA',
    formatter: { precision: 2, unitSuffix: 'kVA' },
  },
  {
    name: 'Kilovolt Ampere Hour',
    asciiSymbol: 'kVAh',
    transform: transformApparentEnergy,
    formatter: { precision: 2, unitSuffix: 'kVAh' },
  },
  {
    name: 'Kilovolt Ampere Reactive',
    asciiSymbol: 'kVAR',
    formatter: { precision: 2, unitSuffix: 'kVAR' },
  },
  {
    name: 'Kilovolt Ampere Hour Reactive',
    asciiSymbol: 'kVARh',
    transform: transformReactiveEnergy,
    formatter: { precision: 2, unitSuffix: 'kVARh' },
  },
  {
    name: 'Horse Power',
    asciiSymbol: 'HP',
    transform: transformPower,
    formatter: { precision: 1, unitSuffix: 'HP' },
  },
  {
    name: 'Power Factor',
    asciiSymbol: 'PF',
    formatter: { precision: 3, unitSuffix: 'PF' },
  },
  {
    name: 'Power Meter Pulse Factor',
    asciiSymbol: 'kWh/pulse',
    formatter: { precision: 0, unitSuffix: 'kWh/pulse' },
  },
  {
    name: 'Megawatt',
    asciiSymbol: 'MW',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'MW' },
  },
  {
    name: 'Megawatt Hour',
    asciiSymbol: 'MWh',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'MWh' },
  },
  {
    name: 'Gigawatt',
    asciiSymbol: 'GW',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'GW' },
  },
  {
    name: 'Gigawatt Hour',
    asciiSymbol: 'GWh',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'GWh' },
  },
  {
    name: 'Terawatt',
    asciiSymbol: 'TW',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'TW' },
  },
  {
    name: 'Terawatt Hour',
    asciiSymbol: 'TWh',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'TWh' },
  },
  {
    name: 'Megawatt Demand',
    asciiSymbol: 'MW_demand',
    aliases: ['MW demand'],
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'MW demand' },
  },
  {
    name: 'Megavolt Ampere',
    asciiSymbol: 'MVA',
    formatter: { precision: 2, unitSuffix: 'MVA' },
  },
  {
    name: 'Megavolt Ampere Reactive',
    asciiSymbol: 'MVAR',
    formatter: { precision: 2, unitSuffix: 'MVAR' },
  },
  {
    name: 'Megavolt Ampere Hour',
    asciiSymbol: 'MVAh',
    transform: transformApparentEnergy,
    formatter: { precision: 2, unitSuffix: 'MVAh' },
  },
  {
    name: 'Megavolt Ampere Hour Reactive',
    asciiSymbol: 'MVARh',
    transform: transformReactiveEnergy,
    formatter: { precision: 2, unitSuffix: 'MVARh' },
  },
  {
    name: 'British Thermal Unit',
    asciiSymbol: 'BTU',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'BTU' },
  },
  {
    name: 'Million BTU',
    asciiSymbol: 'MMBTU',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'MMBTU' },
  },
  {
    name: 'Joules',
    asciiSymbol: 'J',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'J' },
  },
  {
    name: 'Kilojoules',
    asciiSymbol: 'kJ',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'kJ' },
  },
  {
    name: 'Megajoules',
    asciiSymbol: 'MJ',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'MJ' },
  },
  {
    name: 'Calories',
    asciiSymbol: 'cal',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'cal' },
  },
  {
    name: 'Kilocalories',
    asciiSymbol: 'kcal',
    transform: transformEnergy,
    formatter: { precision: 1, unitSuffix: 'kcal' },
  },
  {
    name: 'Watt Hour',
    asciiSymbol: 'Wh',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'Wh' },
  },
  {
    name: 'Kilowatt Demand',
    asciiSymbol: 'kW_demand',
    aliases: ['kW demand'],
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'kW demand' },
  },
  {
    name: 'British Thermal Unit per hour',
    asciiSymbol: 'BTU/h',
    transform: transformPower,
    formatter: { precision: 1, unitSuffix: 'BTU/h' },
  },
  {
    name: 'Therm (100,000 BTU)',
    asciiSymbol: 'therm',
    transform: transformEnergy,
    formatter: { precision: 2, unitSuffix: 'therm' },
  },
  {
    name: 'Tons of Refrigeration',
    asciiSymbol: 'TR',
    transform: transformPower,
    formatter: { precision: 2, unitSuffix: 'TR' },
  },
  {
    name: 'kW/TR',
    asciiSymbol: 'kW/TR',
    formatter: { precision: 2, unitSuffix: 'kW/TR' },
  },

  // Chemistry & Concentration
  {
    name: 'Acidity',
    asciiSymbol: 'pH',
    formatter: { precision: 2, unitSuffix: 'pH' },
  },
  {
    name: 'Parts per million',
    asciiSymbol: 'ppm',
    transform: transformConcentration,
    formatter: { precision: 1, unitSuffix: 'PPM' },
  },
  {
    name: 'Milligrams per liter',
    asciiSymbol: 'mg/L',
    transform: transformConcentration,
    formatter: { precision: 2, unitSuffix: 'mg/L' },
  },
  {
    name: 'Parts per billion',
    asciiSymbol: 'ppb',
    transform: transformConcentration,
    formatter: { precision: 1, unitSuffix: 'ppb' },
  },
  {
    name: 'Micrograms per liter',
    asciiSymbol: 'ug/L',
    aliases: ['µg/L'],
    transform: transformConcentration,
    formatter: { precision: 2, unitSuffix: 'µg/L' },
  },
  {
    name: 'Grams per liter',
    asciiSymbol: 'g/L',
    transform: transformConcentration,
    formatter: { precision: 2, unitSuffix: 'g/L' },
  },
  {
    name: 'Moles per liter (molarity)',
    asciiSymbol: 'mol/L',
    formatter: { precision: 3, unitSuffix: 'mol/L' },
  },
  {
    name: 'Milliequivalents per liter',
    asciiSymbol: 'meq/L',
    formatter: { precision: 2, unitSuffix: 'meq/L' },
  },
  {
    name: 'Oxidation-reduction potential (mV)',
    asciiSymbol: 'ORP',
    formatter: { precision: 0, unitSuffix: 'ORP' },
  },
  {
    name: 'Percent by volume',
    asciiSymbol: '%v/v',
    formatter: { precision: 1, noSpace: true, unitSuffix: '%v/v' },
  },
  {
    name: 'Percent by weight',
    asciiSymbol: '%w/w',
    formatter: { precision: 1, noSpace: true, unitSuffix: '%w/w' },
  },

  // Other
  {
    name: 'Revolutions per minute',
    asciiSymbol: 'rpm',
    formatter: { precision: 0, unitSuffix: 'RPM' },
  },
  {
    name: 'Count',
    asciiSymbol: 'count',
    formatter: { precision: 0, unitSuffix: 'count' },
  },
  {
    name: 'Percentage',
    asciiSymbol: '%',
    aliases: ['percent'],
    formatter: { precision: 1, noSpace: true, unitSuffix: '%' },
  },
  {
    name: 'Ratio (dimensionless)',
    asciiSymbol: 'ratio',
    formatter: { precision: 3, unitSuffix: 'ratio' },
  },

  // Economics
  {
    name: 'US Dollars',
    asciiSymbol: 'USD',
    aliases: ['$'],
    formatter: { precision: 2, unitPrefix: '$' },
  },
  {
    name: 'Dollars per Kilowatt',
    asciiSymbol: 'USD/kW',
    aliases: ['$/kW'],
    formatter: {
      precision: 2,
      noSpace: true,
      unitPrefix: '$',
      unitSuffix: '/kW',
    },
  },
  {
    name: 'Dollars per Kilowatt Hour',
    asciiSymbol: 'USD/kWh',
    aliases: ['$/kWh'],
    formatter: {
      precision: 4,
      noSpace: true,
      unitPrefix: '$',
      unitSuffix: '/kWh',
    },
  },
  {
    name: 'Dollars per million BTU',
    asciiSymbol: 'USD/MMBTU',
    aliases: ['$/MMBTU'],
    formatter: {
      precision: 2,
      noSpace: true,
      unitPrefix: '$',
      unitSuffix: '/MMBTU',
    },
  },
  {
    name: 'Dollars per ton',
    asciiSymbol: 'USD/ton',
    aliases: ['$/ton'],
    formatter: {
      precision: 2,
      noSpace: true,
      unitPrefix: '$',
      unitSuffix: '/ton',
    },
  },

  // Mass & Weight
  {
    name: 'Milligrams',
    asciiSymbol: 'mg',
    transform: transformMass,
    formatter: { precision: 1, unitSuffix: 'mg' },
  },
  {
    name: 'Grams',
    asciiSymbol: 'g',
    transform: transformMass,
    formatter: { precision: 1, unitSuffix: 'g' },
  },
  {
    name: 'Kilograms',
    asciiSymbol: 'kg',
    transform: transformMass,
    formatter: { precision: 2, unitSuffix: 'kg' },
  },
  {
    name: 'Metric ton (tonne) = 1,000 kg',
    asciiSymbol: 't',
    transform: transformMass,
    formatter: { precision: 2, unitSuffix: 't' },
  },
  {
    name: 'Ounces',
    asciiSymbol: 'oz',
    transform: transformMass,
    formatter: { precision: 1, unitSuffix: 'oz' },
  },
  {
    name: 'Pounds',
    asciiSymbol: 'lb',
    transform: transformMass,
    formatter: { precision: 2, unitSuffix: 'lb' },
  },
  {
    name: 'US short ton = 2,000 lb',
    asciiSymbol: 'ton',
    transform: transformMass,
    formatter: { precision: 2, unitSuffix: 'ton' },
  },

  // Area
  {
    name: 'Square millimeters',
    asciiSymbol: 'mm2',
    aliases: ['mm²'],
    transform: transformArea,
    formatter: { precision: 1, unitSuffix: 'mm²' },
  },
  {
    name: 'Square centimeters',
    asciiSymbol: 'cm2',
    aliases: ['cm²'],
    transform: transformArea,
    formatter: { precision: 1, unitSuffix: 'cm²' },
  },
  {
    name: 'Square meters',
    asciiSymbol: 'm2',
    aliases: ['m²'],
    transform: transformArea,
    formatter: { precision: 1, unitSuffix: 'm²' },
  },
  {
    name: 'Square kilometers',
    asciiSymbol: 'km2',
    aliases: ['km²'],
    transform: transformArea,
    formatter: { precision: 2, unitSuffix: 'km²' },
  },
  {
    name: 'Hectares',
    asciiSymbol: 'ha',
    transform: transformArea,
    formatter: { precision: 2, unitSuffix: 'ha' },
  },
  {
    name: 'Square inches',
    asciiSymbol: 'in2',
    aliases: ['in²'],
    transform: transformArea,
    formatter: { precision: 1, unitSuffix: 'in²' },
  },
  {
    name: 'Square feet',
    asciiSymbol: 'ft2',
    aliases: ['ft²'],
    transform: transformArea,
    formatter: { precision: 1, unitSuffix: 'ft²' },
  },
  {
    name: 'Acres',
    asciiSymbol: 'ac',
    transform: transformArea,
    formatter: { precision: 2, unitSuffix: 'ac' },
  },

  // Force
  {
    name: 'Newtons',
    asciiSymbol: 'N',
    transform: transformForce,
    formatter: { precision: 1, unitSuffix: 'N' },
  },
  {
    name: 'Kilonewtons',
    asciiSymbol: 'kN',
    transform: transformForce,
    formatter: { precision: 2, unitSuffix: 'kN' },
  },
  {
    name: 'Pounds-force',
    asciiSymbol: 'lbf',
    transform: transformForce,
    formatter: { precision: 1, unitSuffix: 'lbf' },
  },
  {
    name: 'Kilograms-force',
    asciiSymbol: 'kgf',
    transform: transformForce,
    formatter: { precision: 1, unitSuffix: 'kgf' },
  },

  // Torque
  {
    name: 'Newton meters',
    asciiSymbol: 'N*m',
    aliases: ['N·m'],
    transform: transformTorque,
    formatter: { precision: 1, unitSuffix: 'N·m' },
  },
  {
    name: 'Kilonewton meters',
    asciiSymbol: 'kN*m',
    aliases: ['kN·m'],
    transform: transformTorque,
    formatter: { precision: 2, unitSuffix: 'kN·m' },
  },
  {
    name: 'Pound-force feet',
    asciiSymbol: 'lbf*ft',
    aliases: ['lbf·ft'],
    transform: transformTorque,
    formatter: { precision: 1, unitSuffix: 'lbf·ft' },
  },
  {
    name: 'Pound-force inches',
    asciiSymbol: 'lbf*in',
    aliases: ['lbf·in'],
    transform: transformTorque,
    formatter: { precision: 1, unitSuffix: 'lbf·in' },
  },

  // Volume (static)
  {
    name: 'Milliliters',
    asciiSymbol: 'mL',
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'mL' },
  },
  {
    name: 'Liters',
    asciiSymbol: 'L',
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'L' },
  },
  {
    name: 'Cubic meters',
    asciiSymbol: 'm3',
    aliases: ['m³'],
    transform: transformVolume,
    formatter: { precision: 2, unitSuffix: 'm³' },
  },
  {
    name: 'Cubic inches',
    asciiSymbol: 'in3',
    aliases: ['in³'],
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'in³' },
  },
  {
    name: 'Cubic feet',
    asciiSymbol: 'ft3',
    aliases: ['ft³'],
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'ft³' },
  },
  {
    name: 'Barrels',
    asciiSymbol: 'bbl',
    transform: transformVolume,
    formatter: { precision: 1, unitSuffix: 'bbl' },
  },

  // Mass Flow
  {
    name: 'Grams per minute',
    asciiSymbol: 'g/min',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'g/min' },
  },
  {
    name: 'Kilograms per minute',
    asciiSymbol: 'kg/min',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'kg/min' },
  },
  {
    name: 'Kilograms per hour',
    asciiSymbol: 'kg/h',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'kg/h' },
  },
  {
    name: 'Metric tonnes per hour',
    asciiSymbol: 't/h',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 't/h' },
  },
  {
    name: 'US (short) tons per hour',
    asciiSymbol: 'ton/h',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'ton/h' },
  },
  {
    name: 'US (short) tons per day',
    asciiSymbol: 'ton/d',
    transform: transformMassFlow,
    formatter: { precision: 1, unitSuffix: 'ton/d' },
  },
  {
    name: 'Pounds per hour',
    asciiSymbol: 'lb/h',
    transform: transformMassFlow,
    formatter: { precision: 2, unitSuffix: 'lb/h' },
  },

  // Density
  {
    name: 'Kilograms per cubic meter',
    asciiSymbol: 'kg/m3',
    aliases: ['kg/m³'],
    transform: transformDensity,
    formatter: { precision: 1, unitSuffix: 'kg/m³' },
  },
  {
    name: 'Grams per cubic centimeter',
    asciiSymbol: 'g/cm3',
    aliases: ['g/cm³'],
    transform: transformDensity,
    formatter: { precision: 3, unitSuffix: 'g/cm³' },
  },
  {
    name: 'Grams per milliliter',
    asciiSymbol: 'g/mL',
    transform: transformDensity,
    formatter: { precision: 3, unitSuffix: 'g/mL' },
  },
  {
    name: 'Pounds per cubic foot',
    asciiSymbol: 'lb/ft3',
    aliases: ['lb/ft³'],
    transform: transformDensity,
    formatter: { precision: 1, unitSuffix: 'lb/ft³' },
  },
  {
    name: 'Pounds per gallon',
    asciiSymbol: 'lb/gal',
    transform: transformDensity,
    formatter: { precision: 2, unitSuffix: 'lb/gal' },
  },

  // Humidity & Moisture
  {
    name: 'Percent relative humidity',
    asciiSymbol: '%RH',
    formatter: { precision: 1, noSpace: true, unitSuffix: '%RH' },
  },
  {
    name: 'Dew point (Celsius)',
    asciiSymbol: 'degC_dp',
    aliases: ['°C dp'],
    formatter: { precision: 1, unitSuffix: '°C dp' },
  },
  {
    name: 'Dew point (Fahrenheit)',
    asciiSymbol: 'degF_dp',
    aliases: ['°F dp'],
    formatter: { precision: 1, unitSuffix: '°F dp' },
  },
  {
    name: 'Water activity (0-1)',
    asciiSymbol: 'aw',
    formatter: { precision: 3, unitSuffix: 'aw' },
  },
  {
    name: 'Specific humidity (g water / kg dry air)',
    asciiSymbol: 'g/kg',
    formatter: { precision: 1, unitSuffix: 'g/kg' },
  },
  {
    name: 'Absolute humidity (g water / m³ air)',
    asciiSymbol: 'g/m3',
    aliases: ['g/m³'],
    formatter: { precision: 1, unitSuffix: 'g/m³' },
  },
  {
    name: 'Percent moisture content',
    asciiSymbol: '%MC',
    formatter: { precision: 1, noSpace: true, unitSuffix: '%MC' },
  },

  // Water Quality
  {
    name: 'Nephelometric turbidity units',
    asciiSymbol: 'NTU',
    formatter: { precision: 1, unitSuffix: 'NTU' },
  },
  {
    name: 'Formazin nephelometric units',
    asciiSymbol: 'FNU',
    formatter: { precision: 1, unitSuffix: 'FNU' },
  },
  {
    name: 'Millisiemens per centimeter',
    asciiSymbol: 'mS/cm',
    formatter: { precision: 2, unitSuffix: 'mS/cm' },
  },
  {
    name: 'Percent saturation (dissolved O₂)',
    asciiSymbol: '%sat',
    aliases: ['% sat'],
    formatter: { precision: 1, noSpace: true, unitSuffix: '% sat' },
  },

  // Angle
  {
    name: 'Degrees (angle)',
    asciiSymbol: 'deg',
    aliases: ['degree', 'degrees', '°'],
    transform: transformAngle,
    formatter: { precision: 1, noSpace: true, unitSuffix: '°' },
  },
  {
    name: 'Radians',
    asciiSymbol: 'rad',
    transform: transformAngle,
    formatter: { precision: 3, unitSuffix: 'rad' },
  },
  {
    name: 'Milliradians',
    asciiSymbol: 'mrad',
    transform: transformAngle,
    formatter: { precision: 1, unitSuffix: 'mrad' },
  },

  // Viscosity
  {
    name: 'Centipoise (dynamic)',
    asciiSymbol: 'cP',
    transform: transformDynamicViscosity,
    formatter: { precision: 1, unitSuffix: 'cP' },
  },
  {
    name: 'Pascal-seconds (dynamic)',
    asciiSymbol: 'Pa*s',
    aliases: ['Pa·s'],
    transform: transformDynamicViscosity,
    formatter: { precision: 3, unitSuffix: 'Pa·s' },
  },
  {
    name: 'Centistokes (kinematic)',
    asciiSymbol: 'cSt',
    transform: transformKinematicViscosity,
    formatter: { precision: 1, unitSuffix: 'cSt' },
  },

  // Light
  {
    name: 'Lux (illuminance)',
    asciiSymbol: 'lx',
    formatter: { precision: 0, unitSuffix: 'lx' },
  },
  {
    name: 'Lumens (luminous flux)',
    asciiSymbol: 'lm',
    formatter: { precision: 0, unitSuffix: 'lm' },
  },

  // Bespoke formatters / type-name entries
  {
    name: 'direction',
    asciiSymbol: 'direction',
    inputUnits: 'deg',
    formatter: formatDirection,
  },

  // Boolean family — formatters suppress the type-name suffix.
  {
    name: 'boolean',
    asciiSymbol: 'boolean',
    transform: transformBoolean,
    formatter: formatUnitless,
  },
  {
    name: 'on-off',
    asciiSymbol: 'on-off',
    transform: transformOnOff,
    formatter: formatUnitless,
  },
  {
    name: 'open-closed',
    asciiSymbol: 'open-closed',
    transform: transformOpenClosed,
    formatter: formatUnitless,
  },

  // None / string / number — formatters suppress the type-name suffix.
  {
    name: 'none',
    asciiSymbol: 'none',
    transform: transformNone,
    formatter: formatUnitless,
  },
  {
    name: 'string',
    asciiSymbol: 'string',
    formatter: formatUnitless,
  },
  {
    name: 'number',
    asciiSymbol: 'number',
    formatter: formatUnitless,
  },

  {
    name: 'compact-number',
    asciiSymbol: 'compact-number',
    formatter: formatCompactNumber,
  },

  // PID — bespoke small-decimal truncation.
  {
    name: 'PID',
    asciiSymbol: 'PID',
    formatter: formatPid,
  },
];
